import {
  Briefcase,
  ChevronUp,
  Code,
  Contact,
  Download,
  GraduationCap,
  Linkedin,
  Mail,
  Phone,
  Send
} from 'lucide-react';
import React from 'react';

/**
 * CV Expanded Section Component
 * Based on conversation-mockup.html design
 * Displays detailed CV information inline
 *
 * @param {Object} props
 * @param {Object} props.candidate - Candidate data
 * @param {Function} props.onCollapse - Collapse callback
 * @param {boolean} props.showQuickActions - Show quick action buttons
 */
export function CVExpandedSection({
  candidate,
  onCollapse,
  showQuickActions = true
}) {
  const formatPhoneNumbers = (phoneNumbers) => {
    if (!phoneNumbers || !Array.isArray(phoneNumbers) || phoneNumbers.length === 0) {
      return 'Not provided';
    }
    return phoneNumbers.join(', ');
  };

  const formatLinkedIn = (linkedinUrl) => {
    if (!linkedinUrl) return null;
    const url = linkedinUrl.startsWith('http') ? linkedinUrl : `https://www.linkedin.com/in/${linkedinUrl}`;
    return url;
  };

  return (
    <>
      {/* Quick Actions Bar */}
      {showQuickActions && (
        <div className="flex flex-wrap gap-2 mb-4 pb-3 border-b border-gray-100">
          <button className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 transition flex items-center gap-2">
            <Phone className="w-3 h-3" />
            Schedule Call
          </button>
          <button className="px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-xs font-medium hover:bg-green-100 transition flex items-center gap-2">
            <Mail className="w-3 h-3" />
            Send Email
          </button>
          <button className="px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-xs font-medium hover:bg-purple-100 transition flex items-center gap-2">
            <Download className="w-3 h-3" />
            Download CV
          </button>
          <button className="px-3 py-1.5 bg-orange-50 text-orange-700 rounded-lg text-xs font-medium hover:bg-orange-100 transition flex items-center gap-2">
            <Send className="w-3 h-3" />
            Send to Client
          </button>
        </div>
      )}

      {/* CV Content Sections */}
      <div className="space-y-4">
        {/* Contact Information */}
        <div className="bg-gray-50 rounded-lg p-3">
          <h4 className="font-semibold text-gray-900 mb-2 text-sm flex items-center gap-2">
            <Contact className="w-3.5 h-3.5 text-blue-600" />
            Contact Information
          </h4>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <p className="text-gray-500 mb-0.5">Email</p>
              <p className="font-medium text-gray-900 break-all">
                {candidate.email || 'No email provided'}
              </p>
            </div>
            <div>
              <p className="text-gray-500 mb-0.5">Phone</p>
              <p className="font-medium text-gray-900">
                {formatPhoneNumbers(candidate.phoneNumbers || candidate.phone_numbers)}
              </p>
            </div>
            {candidate.location && (
              <div>
                <p className="text-gray-500 mb-0.5">Location</p>
                <p className="font-medium text-gray-900">
                  {typeof candidate.location === 'string'
                    ? candidate.location
                    : `${candidate.location.city}, ${candidate.location.country}`}
                </p>
              </div>
            )}
            {candidate.linkedinUrl && (
              <div>
                <p className="text-gray-500 mb-0.5">LinkedIn</p>
                <a
                  href={formatLinkedIn(candidate.linkedinUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Linkedin className="w-3 h-3" />
                  Profile
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Summary/Excerpt */}
        {candidate.excerpt && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-2 text-sm">Summary</h4>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-700 leading-relaxed">
                {candidate.excerpt.length > 1000
                  ? `${candidate.excerpt.substring(0, 1000)}...`
                  : candidate.excerpt}
              </p>
            </div>
          </div>
        )}

        {/* Work Experience */}
        {candidate.workExperience && Array.isArray(candidate.workExperience) && candidate.workExperience.length > 0 && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-2 text-sm flex items-center gap-2">
              <Briefcase className="w-3.5 h-3.5 text-blue-600" />
              Work Experience
            </h4>
            <div className="space-y-2">
              {candidate.workExperience.map((job, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-3 hover:border-blue-300 transition"
                >
                  <div className="flex justify-between items-start mb-1">
                    <div>
                      <h5 className="font-semibold text-gray-900 text-sm">{job.title}</h5>
                      <p className="text-xs text-gray-600">{job.company}</p>
                    </div>
                    {job.start_date && (
                      <span className="text-xs text-gray-500">
                        {job.start_date} – {job.end_date || 'Present'}
                      </span>
                    )}
                  </div>
                  {job.description && (
                    <div className="text-xs text-gray-700 mt-2">
                      {typeof job.description === 'string' ? (
                        <ul className="list-disc list-inside space-y-0.5">
                          {job.description.split('\n').filter(line => line.trim()).map((line, i) => (
                            <li key={i}>{line}</li>
                          ))}
                        </ul>
                      ) : (
                        <p>{String(job.description)}</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Skills */}
        <div>
          <h4 className="font-semibold text-gray-900 mb-2 text-sm flex items-center gap-2">
            <Code className="w-3.5 h-3.5 text-blue-600" />
            Skills & Technologies
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {(candidate.matchingContext?.key_matches || candidate.rating?.key_matches || []).map((skill, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full"
              >
                {skill}
              </span>
            ))}
            {(candidate.matchingContext?.key_matches || candidate.rating?.key_matches || []).length === 0 && (
              <span className="text-xs text-gray-500">No skills listed</span>
            )}
          </div>
        </div>

        {/* Education */}
        {candidate.education && Array.isArray(candidate.education) && candidate.education.length > 0 && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-2 text-sm flex items-center gap-2">
              <GraduationCap className="w-3.5 h-3.5 text-blue-600" />
              Education
            </h4>
            {candidate.education.map((edu, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-3">
                <h5 className="font-semibold text-gray-900 text-sm">{edu.degree}</h5>
                <p className="text-xs text-gray-600">
                  {edu.institution} • {edu.start_year} - {edu.end_year || 'Present'}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Collapse Button */}
      <div className="text-center pt-3 mt-3 border-t border-gray-100">
        <button
          onClick={onCollapse}
          className="text-sm text-gray-600 hover:text-gray-900 font-medium flex items-center gap-1 mx-auto transition"
        >
          <ChevronUp className="w-4 h-4" />
          Hide CV Details
        </button>
      </div>
    </>
  );
}

export default CVExpandedSection;

